import React from 'react';
import { isPastSeason } from '../utils/activeSeasonUtil';

const OUTCOME_TITLES = { W: 'Win', L: 'Loss', D: 'Draw' };

const formatPct = (value) => (value ?? 0).toFixed(3).replace(/^0/, '');

const formatDiff = (value) => (value > 0 ? `+${value}` : `${value}`);

function StatTile({ label, value, tone, hint }) {
  const toneClass = tone ? ` kb-stat-tile--${tone}` : '';
  return (
    <div className={`kb-stat-tile${toneClass}`}>
      <span className="kb-stat-label">{label}</span>
      <span className="kb-stat-value">{value}</span>
      {hint && <span className="kb-stat-hint">{hint}</span>}
    </div>
  );
}

function FormDots({ outcomes }) {
  if (!outcomes || outcomes.length === 0) {
    return <span className="kb-form-empty">No results yet</span>;
  }

  return (
    <div className="kb-form-strip">
      <span className="kb-form-strip__label">Last {outcomes.length}</span>
      <div className="kb-form-strip__dots">
        {outcomes.map((outcome, index) => (
          <span
            // Outcomes are a positional sequence, not identified entities.
            key={index}
            className={`kb-dot kb-dot--${outcome.toLowerCase()}`}
            title={OUTCOME_TITLES[outcome] || outcome}
          >
            {outcome}
          </span>
        ))}
      </div>
    </div>
  );
}

function SeasonHistoryRow({ entry, isSelected }) {
  const { season, wins, losses, draws, win_pct: winPct, run_diff: runDiff, games_played: games } = entry;
  const past = isPastSeason(season);

  return (
    <li className={`kb-season-row${isSelected ? ' kb-season-row--active' : ''}`}>
      <div className="kb-season-row__head">
        <span className="kb-season-row__name">{season.name}</span>
        {!past && <span className="kb-season-row__tag">Current</span>}
      </div>
      <div className="kb-season-row__record">
        {wins}-{losses}-{draws}
      </div>
      <div className="kb-season-row__bar">
        <div className="kb-winbar" role="presentation">
          <div className="kb-winbar__fill" style={{ width: `${Math.round((winPct ?? 0) * 100)}%` }} />
        </div>
      </div>
      <div className="kb-season-row__pct">{games > 0 ? formatPct(winPct) : '—'}</div>
      <div className={`kb-season-row__diff${runDiff > 0 ? ' is-pos' : ''}${runDiff < 0 ? ' is-neg' : ''}`}>
        {games > 0 ? formatDiff(runDiff) : '—'}
      </div>
    </li>
  );
}

/**
 * Season stats for a team: headline tiles for the selected season, recent form,
 * and a season-by-season history. All numbers come from /api/teams/<id>/stats/,
 * which derives them from per-game scores rather than the stale
 * Team.wins/losses/draws columns.
 */
function TeamStatsTab({ stats, seasonStats, selectedSeason, loading, error }) {
  if (loading) {
    return <p className="text-muted mb-0">Loading team stats...</p>;
  }

  if (error) {
    return <p className="text-danger mb-0">{error}</p>;
  }

  const seasons = stats?.seasons || [];
  const career = stats?.career;

  if (seasons.length === 0) {
    return <p className="text-muted mb-0">No season stats available for this team yet.</p>;
  }

  const current = seasonStats;
  const hasGames = (current?.games_played || 0) > 0;

  return (
    <div className="kb-team-stats">
      {current ? (
        <>
          <div className="kb-stat-grid">
            <StatTile
              label="Record"
              value={`${current.wins}-${current.losses}-${current.draws}`}
              hint={`${current.games_played} ${current.games_played === 1 ? 'game' : 'games'}`}
            />
            <StatTile label="Win %" value={hasGames ? formatPct(current.win_pct) : '—'} />
            <StatTile label="Runs For" value={current.runs_for} />
            <StatTile label="Runs Against" value={current.runs_against} />
            <StatTile
              label="Run Diff"
              value={hasGames ? formatDiff(current.run_diff) : '—'}
              tone={current.run_diff > 0 ? 'pos' : current.run_diff < 0 ? 'neg' : null}
            />
            <StatTile
              label="Streak"
              value={current.streak ? current.streak.label : '—'}
              tone={current.streak?.type === 'W' ? 'pos' : current.streak?.type === 'L' ? 'neg' : null}
            />
          </div>

          <FormDots outcomes={current.last_5} />

          {current.unresolved > 0 && (
            <p className="kb-stats-note">
              <i className="bi bi-info-circle me-1"></i>
              {current.unresolved} forfeited {current.unresolved === 1 ? 'game has' : 'games have'} no
              recorded result and {current.unresolved === 1 ? 'is' : 'are'} excluded from this record.
            </p>
          )}
        </>
      ) : (
        <p className="text-muted">No stats recorded for the selected season.</p>
      )}

      <div className="kb-stats-section">
        <div className="kb-stats-section__head">
          <h5 className="mb-0">Season History</h5>
          {career && career.games_played > 0 && (
            <span className="kb-stats-section__meta">
              All time {career.wins}-{career.losses}-{career.draws} &middot; {formatPct(career.win_pct)}
            </span>
          )}
        </div>

        <ul className="kb-season-history list-unstyled mb-0">
          {seasons.map((entry) => (
            <SeasonHistoryRow
              key={entry.season.id}
              entry={entry}
              isSelected={String(entry.season.id) === String(selectedSeason)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

export default TeamStatsTab;
